import React, { useState } from 'react';
import api from '../services/api';

const ImportExportPanel = ({ type, title, onImportComplete }) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      console.log(`📤 Exportando ${type}...`);

      const response = await fetch(`/api/backup/export/${type}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al exportar');
      }

      // Obtener el nombre del archivo desde los headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename;

      if (contentDisposition) {
        filename = contentDisposition.split('filename=')[1].replace(/"/g, '');
      } else {
        // Fallback: generar nombre con timestamp si no viene del backend
        const timestamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+/, '');
        filename = `${type}_backup_${timestamp}.json`;
      }

      console.log('📁 Nombre del archivo:', filename);

      // Descargar el archivo
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ ${type} exportados exitosamente`);

    } catch (error) {
      console.error(`❌ Error exportando ${type}:`, error);
      alert(`Error al exportar ${type}: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      setImportResult(null);
      console.log(`📥 Importando ${type}...`);

      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.data) {
        throw new Error('Archivo de backup inválido');
      }

      const response = await api.post(`/api/backup/import/${type}`, {
        data: data.data,
        overwrite
      });

      setImportResult(response.data);
      console.log(`✅ ${type} importados:`, response.data);

      // Notificar al componente padre para actualizar la lista
      if (onImportComplete) {
        onImportComplete();
      }

    } catch (error) {
      console.error(`❌ Error importando ${type}:`, error);
      setImportResult({
        message: `Error al importar ${type}`,
        errors: [error.message]
      });
    } finally {
      setImporting(false);
      // Limpiar el input
      event.target.value = '';
    }
  };

  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      margin: '16px 0',
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>
        📋 Backup de {title}
      </h3>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
        {/* Botón de Exportar */}
        <button
          onClick={handleExport}
          disabled={exporting}
          style={{
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: exporting ? 'not-allowed' : 'pointer',
            opacity: exporting ? 0.7 : 1
          }}
        >
          {exporting ? '⏳ Exportando...' : '📤 Exportar'}
        </button>

        {/* Botón de Importar */}
        <label style={{
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '8px 16px',
          cursor: importing ? 'not-allowed' : 'pointer',
          opacity: importing ? 0.7 : 1,
          display: 'inline-block'
        }}>
          {importing ? '⏳ Importando...' : '📥 Importar'}
          <input
            type="file"
            accept=".json"
            onChange={handleImport}
            disabled={importing}
            style={{ display: 'none' }}
          />
        </label>

        {/* Checkbox de Sobrescribir */}
        <label style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
          />
          Sobrescribir existentes
        </label>
      </div>

      {/* Resultado de importación */}
      {importResult && (
        <div style={{
          padding: '12px',
          borderRadius: '4px',
          backgroundColor: importResult.errors ? '#ffebee' : '#e8f5e8',
          border: `1px solid ${importResult.errors ? '#f44336' : '#4caf50'}`,
          fontSize: '14px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {importResult.errors ? '⚠️' : '✅'} {importResult.message}
          </div>

          {importResult.imported > 0 && (
            <div>📥 Importados: {importResult.imported}</div>
          )}

          {importResult.updated > 0 && (
            <div>🔄 Actualizados: {importResult.updated}</div>
          )}

          {importResult.errors && importResult.errors.length > 0 && (
            <div>
              <div style={{ fontWeight: 'bold', color: '#d32f2f', marginTop: '8px' }}>
                Errores ({importResult.errors.length}):
              </div>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {importResult.errors.slice(0, 5).map((error, index) => (
                  <li key={index} style={{ color: '#d32f2f', fontSize: '13px' }}>
                    {error}
                  </li>
                ))}
                {importResult.errors.length > 5 && (
                  <li style={{ color: '#d32f2f', fontSize: '13px', fontStyle: 'italic' }}>
                    ... y {importResult.errors.length - 5} errores más
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
        💡 <strong>Exportar:</strong> Descarga un archivo JSON con la configuración actual<br/>
        💡 <strong>Importar:</strong> Carga configuración desde un archivo JSON de backup
      </div>
    </div>
  );
};

export default ImportExportPanel;